import { getUniswapPool } from "contracts/util";
import type {
  ApiLiquidationPriceResponse,
  ApiPoolAprResponse,
  ApiPoolPnlResponse,
  ApiResult,
  ApiSwapCandlesResponse,
  ApiTotalsResponse,
  ChainSearchResponse,
} from "types/api";

// TODO: refactor to simple const?
export const getBaseUrl = () =>
  process.env.NEXT_PUBLIC_API_BASEURL ?? "https://api.marginly.com/api";

export interface EndpointArgs {
  chainSearch: [number];
  liquidationPrice: [number, `0x${string}`, `0x${string}`];
  poolApr: [number, `0x${string}`];
  poolPnl: [number, `0x${string}`, `0x${string}`];
  swapCandles: [number, `0x${string}`, string | undefined];
  totals: [number, `0x${string}`];
}

export interface EndpointValues {
  chainSearch: ChainSearchResponse;
  liquidationPrice: ApiLiquidationPriceResponse;
  poolApr: ApiPoolAprResponse;
  poolPnl: ApiPoolPnlResponse;
  swapCandles: ApiSwapCandlesResponse;
  totals: ApiTotalsResponse;
}

export type Endpoints = keyof EndpointArgs;

// FIXME: replace chain id constants
export const createEndpointUrl = <T extends Endpoints>({
  args,
  name,
}: {
  args: EndpointArgs[T];
  name: T;
}): string => {
  switch (name) {
    case "chainSearch": {
      const [chainId] = args;
      // eg. https://api.marginly.com/api/chain/search?publicChainId=421613
      return `${getBaseUrl()}/chain/search?publicChainId=${chainId}`;
    }
    case "poolApr": {
      const [internalChainId, pool] = args;

      return `${getBaseUrl()}/chain/${internalChainId}/marginlypool/${pool}/apr`;
    }

    case "swapCandles": {
      const [internalChainId, pool, periodParams] =
        args as EndpointArgs["swapCandles"];

      return `${getBaseUrl()}/chain/${internalChainId}/pool/${getUniswapPool(
        pool,
      )}/swapCandles?${periodParams ?? ""}`;
    }

    case "poolPnl": {
      const [internalChainId, pool, user] = args as EndpointArgs["poolPnl"];
      return `${getBaseUrl()}/chain/${internalChainId}/marginlypool/${pool}/user/${user}/pnl`;
    }

    case "liquidationPrice": {
      const [internalChainId, pool, user] = args as EndpointArgs["poolPnl"];
      return `${getBaseUrl()}/chain/${internalChainId}/marginlypool/${pool}/user/${user}`;
    }

    case "totals": {
      const [internalChainId, pool] = args as EndpointArgs["totals"];
      return `${getBaseUrl()}/chain/${internalChainId}/marginlypool/${pool}`;
    }

    default:
      throw new Error(`unknown endpoint: ${name}`);
  }
};

export const isPoolAprResponse = (raw: any): raw is ApiPoolAprResponse => {
  if (!raw) {
    return false;
  }

  return ["baseApr", "quoteApr"].every((k) => k in raw);
};

export const isSwapCandlesResponse = (
  raw: any,
): raw is ApiSwapCandlesResponse => {
  if (!raw || !Array.isArray(raw.points)) {
    return false;
  }

  if (!raw.points.length) {
    return true;
  }

  // assume that all points have same structure
  if (!Array.isArray(raw.points[0])) {
    return false;
  }

  const [timestamp, point] = raw.points[0];

  if (typeof timestamp !== "number" || typeof point !== "object") {
    return false;
  }

  return [
    "openTimestamp",
    "frame",
    "baseCurrency",
    "quoteCurrency",
    "open",
    "high",
    "low",
    "close",
    "volumeBuy",
    "volumeSell",
  ].every((k) => k in point);
};

export const isPoolPnlResponse = (raw: any): raw is ApiPoolPnlResponse => {
  if (!raw) return false;

  return ["pnl", "positionType", "currentBasePrice", "pnlPoints"].every(
    (k) => k in raw,
  );
};

export const isLiquidationPriceResponse = (
  raw: any,
): raw is ApiLiquidationPriceResponse => {
  if (!raw) return false;

  return ["liquidationPrice"].every((k) => k in raw);
};

export const isChainSearchResponse = (raw: any): raw is ChainSearchResponse =>
  !!raw?.chainInfo;

export const isTotalsResponse = (raw: any): raw is ApiTotalsResponse => {
  if (!raw) return false;

  return ["baseCollateral", "baseDebt", "quoteCollateral", "quoteDebt"].every(
    (k) => k in raw,
  );
};

const validators = {
  chainSearch: isChainSearchResponse,
  poolApr: isPoolAprResponse,
  swapCandles: isSwapCandlesResponse,
  poolPnl: isPoolPnlResponse,
  liquidationPrice: isLiquidationPriceResponse,
  totals: isTotalsResponse,
} as const;

export const validateResponse = <T extends Endpoints>(
  name: T,
  value: any,
): value is EndpointValues[T] => {
  const validator = validators[name];

  if (typeof validator !== "function") {
    throw new Error(`unknown validator: ${name}`);
  }

  return validators[name](value);
};

export const isSuccess = <T extends {}>(
  raw?: ApiResult<any, T>,
): raw is ApiResult<true, T> => raw?.success;
